import moment, { type Moment } from 'moment-timezone';

export function getRangeValue<S extends string | undefined, E extends string | undefined>(
  value?: [S, E],
) {
  return value?.map((it) => (it ? moment(it) : undefined)) as
    | [S extends string ? Moment : undefined, E extends string ? Moment : undefined]
    | undefined;
}

export function getAvailableDateRange(range?: [string | undefined, string | undefined]) {
  const [min, max] = getRangeValue(range) ?? [];
  return [min, max ?? moment()] as [Moment | undefined, Moment | undefined];
}

export function mergeDateTimeWithPrevTime(date: Moment, prev?: Moment) {
  return prev
    ? date
        .clone()
        .hour(prev.hour())
        .minute(prev.minute())
        .second(prev.second())
        .millisecond(prev.millisecond())
    : date;
}

export function mergeDateTimeWithPrevDate(date: Moment, prev?: Moment) {
  return prev ? date.clone().year(prev.year()).month(prev.month()).date(prev.date()) : date;
}

export const getRangeArray = (from: number, to: number) =>
  from > to ? [] : Array.from({ length: to - from + 1 }, (_, i) => from + i);

export function getDisabledTimeHours(
  date: Moment,
  range?: [Moment | undefined, Moment | undefined],
) {
  if (!range) return;

  const disabledHours = new Set<number>();

  if (range[0] && date.isSame(range[0], 'day')) {
    getRangeArray(0, range[0].hour() - 1).forEach((h) => disabledHours.add(h));
  }

  if (range[1] && date.isSame(range[1], 'day')) {
    getRangeArray(range[1].hour() + 1, 23).forEach((h) => disabledHours.add(h));
  }

  if ((range[0] && date.isBefore(range[0], 'day')) || (range[1] && date.isAfter(range[1], 'day'))) {
    getRangeArray(0, 23).forEach((h) => disabledHours.add(h));
  }

  return () => Array.from(disabledHours);
}

export function checkDateValueValid(
  value: [moment.Moment, moment.Moment] | undefined,
  maxLength = Infinity,
) {
  if (!value || !value[0] || !value[1]) return 'NIL';

  if (value[0].isAfter(value[1])) return 'START_AFTER_END';

  if (Math.abs(value[0].diff(value[1], 'milliseconds')) > maxLength) return 'EXCEED_MAX_LENGTH';

  return 'VALID';
}

export function formatDuration(maxLength: number) {
  if (!Number.isFinite(maxLength)) return '';

  const absMs = Math.abs(maxLength);

  if (absMs === 0) return '';

  const duration = moment.duration(absMs);

  const days = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();

  function plural(value: number, unit: string) {
    return `${value} ${unit}${value === 1 ? '' : 's'}`;
  }

  const parts: string[] = [];

  if (days) parts.push(plural(days, 'day'));
  if (hours) parts.push(plural(hours, 'hour'));
  if (minutes) parts.push(plural(minutes, 'minute'));

  return parts.join(' ');
}
